import React, { useState } from "react";

interface UnlockUserModalProps {
  user?: any;
}

const UnlockUserModal: React.FC<UnlockUserModalProps> = ({ user }) => {
  const [unlockData, setUnlockData] = useState({
    reason: "",
    notifyUser: true,
    resetPassword: false,
    addNotes: "",
    temporaryAccess: false,
    accessDuration: "24",
  });

  const handleUnlock = () => {
    // TODO: Unlock user API call
    console.log("Unlocking user:", user?.id, unlockData);
  };

  // Always render modal structure
  if (!user) {
    return (
      <div className="modal fade" id="unlockUserModal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Unlock User Account</h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body">
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading user details...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const unlockReasons = [
    "Account locked due to multiple failed login attempts",
    "Password reset requested by user",
    "Administrative lock - resolved",
    "System maintenance completed",
    "Security clearance approved",
    "Technical issue resolved",
    "User verification completed",
    "Other",
  ];

  return (
    <div className="modal fade" id="unlockUserModal">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">
              <i className="ti ti-lock-open me-2 text-success"></i>Unlock User
              Account
            </h4>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
            />
          </div>
          <div className="modal-body">
            {/* User Information */}
            <div className="alert alert-warning">
              <div className="d-flex align-items-center">
                <i className="ti ti-alert-triangle me-2"></i>
                <div>
                  <strong>Account Status:</strong> {user.lockStatus || "Locked"}
                  <br />
                  <small>
                    Locked since: {user.lockDate || "Unknown"} | Reason:{" "}
                    {user.lockReason || "Multiple failed attempts"}
                  </small>
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <div className="card border">
                  <div className="card-body p-3">
                    <h6 className="mb-2">
                      <i className="ti ti-user me-2"></i>User Details
                    </h6>
                    <table className="table table-borderless table-sm mb-0">
                      <tbody>
                        <tr>
                          <td>
                            <strong>Name:</strong>
                          </td>
                          <td>{user.name}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Email:</strong>
                          </td>
                          <td>{user.email}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Role:</strong>
                          </td>
                          <td>{user.role}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>User ID:</strong>
                          </td>
                          <td>{user.id}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border">
                  <div className="card-body p-3">
                    <h6 className="mb-2">
                      <i className="ti ti-info-circle me-2"></i>Lock Information
                    </h6>
                    <table className="table table-borderless table-sm mb-0">
                      <tbody>
                        <tr>
                          <td>
                            <strong>Failed Attempts:</strong>
                          </td>
                          <td className="text-danger">
                            {user.failedAttempts || 5}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Last Attempt:</strong>
                          </td>
                          <td>{user.lastAttempt || "Today 2:30 PM"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>IP Address:</strong>
                          </td>
                          <td>{user.lastIP || "192.168.1.1"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Device:</strong>
                          </td>
                          <td>{user.lastDevice || "Windows Chrome"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <form>
              <div className="mb-3">
                <label className="form-label">
                  Unlock Reason <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={unlockData.reason}
                  onChange={(e) =>
                    setUnlockData({ ...unlockData, reason: e.target.value })
                  }
                  required
                >
                  <option value="">Select unlock reason</option>
                  {unlockReasons.map((reason, index) => (
                    <option key={index} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Additional Notes</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={unlockData.addNotes}
                  onChange={(e) =>
                    setUnlockData({ ...unlockData, addNotes: e.target.value })
                  }
                  placeholder="Optional notes about the unlock action..."
                />
              </div>

              <div className="mb-3">
                <h6 className="mb-2">Unlock Options</h6>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="notifyUser"
                    checked={unlockData.notifyUser}
                    onChange={(e) =>
                      setUnlockData({
                        ...unlockData,
                        notifyUser: e.target.checked,
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="notifyUser">
                    Send email notification to user
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="resetPassword"
                    checked={unlockData.resetPassword}
                    onChange={(e) =>
                      setUnlockData({
                        ...unlockData,
                        resetPassword: e.target.checked,
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="resetPassword">
                    Force password reset on next login
                  </label>
                </div>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="temporaryAccess"
                    checked={unlockData.temporaryAccess}
                    onChange={(e) =>
                      setUnlockData({
                        ...unlockData,
                        temporaryAccess: e.target.checked,
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="temporaryAccess">
                    Grant temporary access only
                  </label>
                </div>

                {unlockData.temporaryAccess && (
                  <div className="mb-3">
                    <label className="form-label">
                      Access Duration (hours)
                    </label>
                    <select
                      className="form-select"
                      value={unlockData.accessDuration}
                      onChange={(e) =>
                        setUnlockData({
                          ...unlockData,
                          accessDuration: e.target.value,
                        })
                      }
                    >
                      <option value="2">2 hours</option>
                      <option value="6">6 hours</option>
                      <option value="12">12 hours</option>
                      <option value="24">24 hours</option>
                      <option value="48">48 hours</option>
                      <option value="72">72 hours</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="alert alert-info">
                <h6>
                  <i className="ti ti-info-circle me-2"></i>Important
                  Information:
                </h6>
                <ul className="mb-0">
                  <li>User will be able to log in immediately after unlock</li>
                  <li>Failed login attempt counter will be reset</li>
                  <li>All active sessions will remain valid</li>
                  <li>This action will be logged in the audit trail</li>
                </ul>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleUnlock}
              disabled={!unlockData.reason}
            >
              <i className="ti ti-lock-open me-2"></i>Unlock Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnlockUserModal;
